import React, { useState } from 'react';
import { MoveUp, MoveUpRight, MoveRight, MoveDownRight, MoveDown, MoveDownLeft, MoveLeft, MoveUpLeft, Circle } from 'lucide-react';
import { Button } from './ui/button';

// A top-down vehicle section picker. Shares the visual language of
// DamageHotspotSelector (the damage assessment's zone selector), so the three
// places a section is chosen all look and behave the same.
//
// Single-select by default — `value` is a zone id and `onSelect` fires with the
// one clicked. Pass `multiple` and it takes `values` (an array) instead, firing
// `onChange` with the new array: clicking a lit section turns it off. That is
// what the line sheet needs, where one line can belong to both front corners,
// and it is a different question from "which section is this part on", which is
// still a single answer everywhere else.

const SEDAN_IMAGE = `${process.env.PUBLIC_URL || ''}/sedan-top.png`;

const ZONES = [
  { id: 'front', label: 'Front', angle: 0, Icon: MoveDown },
  { id: 'front-right', label: 'Front right', angle: 45, Icon: MoveDownLeft },
  { id: 'right', label: 'Right side', angle: 90, Icon: MoveLeft },
  { id: 'rear-right', label: 'Rear right', angle: 135, Icon: MoveUpLeft },
  { id: 'rear', label: 'Rear', angle: 180, Icon: MoveUp },
  { id: 'rear-left', label: 'Rear left', angle: 225, Icon: MoveUpRight },
  { id: 'left', label: 'Left side', angle: 270, Icon: MoveRight },
  { id: 'front-left', label: 'Front left', angle: 315, Icon: MoveDownRight },
  { id: 'roof', label: 'Roof', angle: null, Icon: Circle },
  { id: 'underside', label: 'Underside', angle: null, Icon: null },
  { id: 'other', label: 'Other', angle: null, Icon: null },
];

const ARROW_POSITIONS = {
  front: { top: '20%', left: '50%' },
  'front-right': { top: '26%', left: '74%' },
  right: { top: '50%', left: '80%' },
  'rear-right': { top: '74%', left: '74%' },
  rear: { top: '80%', left: '50%' },
  'rear-left': { top: '74%', left: '26%' },
  left: { top: '50%', left: '20%' },
  'front-left': { top: '26%', left: '26%' },
  roof: { top: '50%', left: '50%' },
};

const CLICKABLE_AREAS = {
  front: { top: '15%', left: '35%', width: '30%', height: '20%' },
  'front-right': { top: '20%', left: '60%', width: '25%', height: '25%' },
  right: { top: '40%', left: '65%', width: '25%', height: '20%' },
  'rear-right': { top: '60%', left: '60%', width: '25%', height: '25%' },
  rear: { top: '65%', left: '35%', width: '30%', height: '20%' },
  'rear-left': { top: '60%', left: '15%', width: '25%', height: '25%' },
  left: { top: '40%', left: '10%', width: '25%', height: '20%' },
  'front-left': { top: '20%', left: '15%', width: '25%', height: '25%' },
  roof: { top: '40%', left: '40%', width: '20%', height: '20%' },
};

const buildWedgeStyle = (angle, intensity, zoneId) => {
  if (!intensity) return { opacity: 0 };
  const isLeftOrRight = zoneId === 'left' || zoneId === 'right';
  const isCorner = zoneId.includes('-');
  let maskGradient;
  if (isLeftOrRight) maskGradient = 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0) 60%)';
  else if (isCorner) maskGradient = 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0) 70%)';
  else maskGradient = 'radial-gradient(circle at 50% 50%, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0) 80%)';
  const start = angle - 22.5;
  const colour = `rgba(56, 189, 248, ${intensity})`;
  return {
    opacity: 1,
    background: `conic-gradient(from ${start}deg at 50% 50%, ${colour} 0deg 45deg, rgba(56, 189, 248, 0) 48deg 360deg)`,
    filter: `blur(${intensity > 0.5 ? 18 : 12}px)`,
    maskImage: maskGradient,
  };
};

const buildRoofGlowStyle = (intensity) => {
  if (!intensity) return { opacity: 0 };
  return {
    opacity: 1,
    background: `radial-gradient(circle at 50% 50%, rgba(56, 189, 248, ${intensity}) 0%, rgba(56, 189, 248, 0) 40%)`,
    filter: `blur(${intensity > 0.5 ? 16 : 10}px)`,
  };
};

const VehicleSurface = () => (
  <div className="absolute inset-[8%] overflow-hidden bg-transparent flex items-center justify-center">
    <img src={SEDAN_IMAGE} alt="" className="pointer-events-none object-contain" style={{ width: '70%', height: '70%' }} />
  </div>
);

const VehicleSectionPicker = ({
  value = 'other',
  onSelect,
  multiple = false,
  values = [],
  onChange,
}) => {
  const [hoveredZone, setHoveredZone] = useState(null);

  const selected = multiple ? new Set(values) : new Set(value ? [value] : []);
  const isSelected = (id) => selected.has(id);

  // In multi-select a second click on a lit section turns it off, which is the
  // only way to correct a mis-click without a separate clear control.
  const toggle = (id) => {
    if (!multiple) { onSelect?.(id); return; }
    const next = new Set(values);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange?.([...next]);
  };

  return (
    <div className="group/selector w-52">
      <div className="relative isolate w-full aspect-[2/3] overflow-visible">
        <div className="pointer-events-none absolute inset-[8%]">
          {ZONES.filter((z) => z.Icon).map((zone) => {
            const isActive = isSelected(zone.id);
            const isHovered = hoveredZone === zone.id && !isActive;
            const intensity = isActive ? 0.7 : isHovered ? 0.35 : 0;
            if (zone.id === 'roof') {
              return <div key={`wedge-${zone.id}`} className="absolute inset-0 transition-opacity duration-200 ease-out" style={buildRoofGlowStyle(intensity)} />;
            }
            return <div key={`wedge-${zone.id}`} className="absolute inset-0 transition-opacity duration-200 ease-out" style={buildWedgeStyle(zone.angle, intensity, zone.id)} />;
          })}
        </div>

        <VehicleSurface />

        {ZONES.filter((z) => z.Icon).map((zone) => {
          const isActive = isSelected(zone.id);
          const clickableArea = CLICKABLE_AREAS[zone.id];
          const arrowPosition = ARROW_POSITIONS[zone.id];
          const Icon = zone.Icon;
          return (
            <React.Fragment key={zone.id}>
              <button
                type="button"
                aria-pressed={isActive}
                aria-label={zone.label}
                title={zone.label}
                onClick={() => toggle(zone.id)}
                onMouseEnter={() => setHoveredZone(zone.id)}
                onMouseLeave={() => setHoveredZone(null)}
                className="absolute cursor-pointer transition-opacity duration-200"
                style={{ top: clickableArea.top, left: clickableArea.left, width: clickableArea.width, height: clickableArea.height }}
              />
              <div
                className={`transition-all duration-200 pointer-events-none ${
                  isActive ? 'text-sky-500 opacity-100' : hoveredZone === zone.id ? 'text-sky-500 opacity-100' : 'text-slate-400 opacity-0 group-hover/selector:opacity-30'
                }`}
                style={{ top: arrowPosition.top, left: arrowPosition.left, transform: 'translate(-50%, -50%)', position: 'absolute' }}
              >
                <Icon size={22} strokeWidth={2.5} />
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className="mt-3 flex flex-col gap-1.5">
        {ZONES.filter((z) => !z.Icon).map((zone) => {
          const isActive = isSelected(zone.id);
          return (
            <Button
              key={zone.id}
              type="button"
              variant="outline"
              size="sm"
              aria-pressed={isActive}
              onClick={() => toggle(zone.id)}
              className={`w-full transition-colors text-xs ${
                isActive ? 'bg-sky-100 border-sky-500 text-sky-700 hover:bg-sky-200' : 'text-slate-600 hover:text-sky-600 hover:border-sky-400'
              }`}
            >
              {zone.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default VehicleSectionPicker;
